import React from "react";
import { useLocation } from "react-router-dom";
import { Box, useBreakpointValue } from "@chakra-ui/react";
import LunaChatbot from "./Luna/LunaChatbot";
import { useEntitlements } from "../hooks/useEntitlements";
import useDashboardNavWidth, {
  DASHBOARD_TOPBAR_H,
  DASHBOARD_FRAME_GAP,
  DASHBOARD_FRAME_RADIUS,
  DASHBOARD_BACKDROP,
} from "../hooks/layout/useDashboardNavWidth";

// Height of the page card below the top bar (the bar is the card's header).
const CARD_BODY_H = `calc(100vh - ${DASHBOARD_TOPBAR_H + DASHBOARD_FRAME_GAP * 2}px)`;

// Desktop: page roots are written with minH="100vh", which would add the top
// bar height as dead scroll on every short page. The page root (first child)
// is capped to the space left in the card instead — one rule here rather
// than an override in each page.
const desktopPageRootSx = {
  "& > :first-of-type": { minHeight: CARD_BODY_H },
};

const DashboardPageContainer = ({ children }) => {
  const navWidth = useDashboardNavWidth();
  const location = useLocation();
  // Luna admin copilot bubble = inbox module. Without it, the bubble is hidden
  // entirely (the backend /luna mount 403s for these companies anyway).
  const { hasModule } = useEntitlements();

  // Mobile detection - mobile is base and sm (up to 480px), desktop is md and above
  const isMobile = useBreakpointValue({ base: true, sm: true, md: false, lg: false, xl: false });

  if (isMobile) {
    // Top padding clears the fixed mobile header — safe-area aware so content
    // is never hidden when the bar grows under the iPhone notch/Dynamic Island
    // (PWA/standalone mode).
    return (
      <Box minH="100vh" bg="#F7FAFC" pt="calc(env(safe-area-inset-top, 0px) + 70px)">
        {children}
        {hasModule('inbox') && <LunaChatbot currentPath={location.pathname} />}
      </Box>
    );
  }

  // Desktop: the page is a white card floating on the tinted backdrop, to the
  // right of the sidebar rail; the fixed top bar is the card's header. The
  // window keeps the scroll (sticky page headers rely on it), so the card
  // clips with `overflow: clip`, which does not create a scroll container.
  return (
    <Box
      minH="100vh"
      bg="#F2F5FA"
      bgImage={DASHBOARD_BACKDROP}
      bgAttachment="fixed"
      pl={`${navWidth + DASHBOARD_FRAME_GAP}px`}
      pr={`${DASHBOARD_FRAME_GAP}px`}
      pt={`${DASHBOARD_FRAME_GAP + DASHBOARD_TOPBAR_H}px`}
      pb={`${DASHBOARD_FRAME_GAP}px`}
      transition="padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      <Box
        minH={CARD_BODY_H}
        bg="white"
        border="1px solid"
        borderTop="none"
        borderColor="rgba(0, 0, 0, 0.06)"
        borderBottomRadius={DASHBOARD_FRAME_RADIUS}
        overflow="clip"
        sx={desktopPageRootSx}
      >
        {children}
      </Box>
      {hasModule('inbox') && <LunaChatbot currentPath={location.pathname} />}
    </Box>
  );
};

export default DashboardPageContainer;
